/**
 * VariableInspector - Execution flow variable inspector
 * Shows available state variables grouped by execution step
 */
import React, { useState, useCallback, useMemo } from 'react';
import './VariableInspector.css';

const TypeBadge = ({ type }) => {
  const typeColors = {
    string: 'var(--type-string)',
    number: 'var(--type-number)',
    boolean: 'var(--type-boolean)',
    object: 'var(--type-object)',
    array: 'var(--type-array)',
    reference: 'var(--type-reference)',
    unknown: 'var(--type-unknown)'
  };
  
  return (
    <span 
      className="vi-type-badge"
      style={{ '--badge-color': typeColors[type] || 'var(--type-object)' }}
    >
      {type}
    </span>
  );
};

const GroupIcon = ({ icon, stepNumber }) => {
  if (icon === 'input') {
    return (
      <span className="vi-group-icon vi-icon-step" title="Start">
        S
      </span>
    );
  }
  
  return (
    <span className="vi-group-icon vi-icon-step">
      {stepNumber}
    </span>
  );
};

const VariableRow = ({ 
  variable, 
  depth = 0,
  onInsert,
  onSelect,
  selectedPath,
  hasChildren = false,
  isExpanded = false,
  onToggleExpand
}) => {
  const isSelected = selectedPath === variable.path;
  const displayName = variable.path.includes('.') 
    ? variable.path.split('.').pop() 
    : variable.path;
  
  const handleInsert = useCallback((e) => {
    e.stopPropagation();
    onInsert(variable.path);
  }, [onInsert, variable.path]);

  const handleSelect = useCallback((e) => {
    e.stopPropagation();
    if (onSelect) {
      onSelect(variable.path, variable);
    }
  }, [onSelect, variable]);

  const handleToggle = useCallback((e) => {
    e.stopPropagation();
    if (hasChildren && onToggleExpand) {
      onToggleExpand(variable.path);
    }
  }, [hasChildren, onToggleExpand, variable.path]);
  
  return (
    <div 
      className={`vi-node-row ${isSelected ? 'selected' : ''}`}
      onClick={handleSelect}
    >
      {/* Render indent guides - always show at least one for root items */}
      {Array.from({ length: depth + 1 }, (_, i) => (
        <span key={i} className={`vi-indent-guide ${i === depth ? 'vi-indent-guide--last' : ''}`} />
      ))}
      {hasChildren ? (
        <span 
          className={`vi-expand-icon vi-expand-toggle ${isExpanded ? 'expanded' : ''}`}
          onClick={handleToggle}
        >
          ▶
        </span>
      ) : (
        <span className="vi-expand-placeholder" />
      )}
      <span className="vi-node-name" title={variable.path}>{displayName}</span>
      <TypeBadge type={variable.type} />
      {variable.optional && <span className="vi-optional">?</span>}
      <button 
        className="vi-insert-btn"
        onClick={handleInsert}
        title={`Insert @${variable.path}`}
      >
        @
      </button>
    </div>
  );
};

const VariableGroup = ({ 
  group, 
  onInsert, 
  onSelect, 
  selectedPath, 
  defaultExpanded = true 
}) => {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);
  
  // Auto-expand first 2 levels of nested properties by default
  const initialExpandedPaths = useMemo(() => {
    const expanded = new Set();
    for (const v of group.variables) {
      const parts = v.path.split('.');
      // Auto-expand first and second level objects
      // (e.g., 'codeImplementation' and 'codeImplementation.metrics')
      if (parts.length <= 2 && v.type === 'object') {
        expanded.add(v.path);
      }
    }
    return expanded;
  }, [group.variables]);
  
  const [expandedPaths, setExpandedPaths] = useState(initialExpandedPaths);
  
  const togglePath = useCallback((path) => {
    setExpandedPaths(prev => {
      const next = new Set(prev);
      if (next.has(path)) {
        next.delete(path);
      } else {
        next.add(path);
      }
      return next;
    });
  }, []);
  
  const renderVariables = useCallback(() => {
    const rendered = [];
    const processedPaths = new Set();
    
    // Build parent-child relationships
    const childrenOf = new Map();
    for (const v of group.variables) {
      const parts = v.path.split('.');
      if (parts.length > 1) {
        const parentPath = parts.slice(0, -1).join('.');
        if (!childrenOf.has(parentPath)) {
          childrenOf.set(parentPath, []);
        }
        childrenOf.get(parentPath).push(v.path);
      }
    }
    
    for (const v of group.variables) {
      if (processedPaths.has(v.path)) continue;
      processedPaths.add(v.path);
      
      const parts = v.path.split('.');
      const depth = Math.max(0, parts.length - 1);
      
      // Check if any parent is collapsed
      let isHidden = false;
      for (let i = 1; i < parts.length; i++) {
        const ancestorPath = parts.slice(0, i).join('.');
        if (!expandedPaths.has(ancestorPath)) {
          isHidden = true;
          break;
        }
      }
      
      if (isHidden) continue;
      
      const hasChildren = childrenOf.has(v.path);
      const isPathExpanded = expandedPaths.has(v.path);
      
      rendered.push(
        <VariableRow
          key={v.path}
          variable={v}
          depth={depth}
          onInsert={onInsert}
          onSelect={onSelect}
          selectedPath={selectedPath}
          hasChildren={hasChildren}
          isExpanded={isPathExpanded}
          onToggleExpand={togglePath}
        />
      );
    }
    
    return rendered;
  }, [group.variables, onInsert, onSelect, selectedPath, expandedPaths, togglePath]);
  
  if (group.variables.length === 0) return null;
  
  return (
    <div className="vi-group">
      <div 
        className="vi-group-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <span className={`vi-expand-icon ${isExpanded ? 'expanded' : ''}`}>
          ▶
        </span>
        <GroupIcon icon={group.icon} stepNumber={group.stepNumber} />
        <span className="vi-group-title">{group.label}</span>
        {group.hasSchema && group.icon === 'input' && (
          <span className="vi-schema-badge" title="Schema defined in workflow">✓</span>
        )}
        <span className="vi-group-count">{group.variables.length}</span>
      </div>
      
      {isExpanded && (
        <div className="vi-group-content">
          {renderVariables()}
        </div>
      )}
    </div>
  );
};

export const VariableInspector = ({ 
  groups = [],
  availableVariables = {},
  onInsertVariable,
  onSelectVariable,
  selectedPath
}) => {
  const displayGroups = useMemo(() => {
    if (groups.length > 0) {
      return groups;
    }
    
    const baseContext = [];
    const nodeOutputs = [];
    
    for (const [key, value] of Object.entries(availableVariables)) {
      const variable = {
        path: key,
        type: value.type || 'object',
        label: value.label || key,
        optional: false
      };
      
      if (key.includes('_output') || key.includes('_raw')) {
        nodeOutputs.push(variable);
      } else {
        baseContext.push(variable);
      }
    }
    
    return [
      { id: 'base', label: 'Base Context', icon: 'input', stepNumber: 0, variables: baseContext },
      { id: 'outputs', label: 'Node Outputs', icon: 'node', stepNumber: 1, variables: nodeOutputs }
    ].filter(g => g.variables.length > 0);
  }, [groups, availableVariables]);
  
  const handleInsert = useCallback((path) => {
    if (onInsertVariable) {
      onInsertVariable(`@${path}`);
    }
  }, [onInsertVariable]);
  
  const handleSelect = useCallback((path, variable) => {
    if (onSelectVariable) {
      onSelectVariable(path, variable);
    }
  }, [onSelectVariable]);
  
  return (
    <div className="variable-inspector">
      <div className="vi-body">
        {displayGroups.map((group, index) => (
          <VariableGroup
            key={group.id}
            group={group}
            onInsert={handleInsert}
            onSelect={handleSelect}
            selectedPath={selectedPath}
            defaultExpanded={index === 0}
          />
        ))}
      </div>
      
      <div className="vi-hint">
        <span className="vi-at-symbol">@</span> to insert
      </div>
    </div>
  );
};

export default VariableInspector;
